import { Button, Container, FlexGrid, Header, Image, Loader, Modal, Panel, Visibility, Divider } from '@gasbuddy/react-components';
import { useEffectWithDeepCompare } from '@gasbuddy/react-hooks';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { localyticsEvent, noop, scrollToTop } from '../../../lib/utils';
import { getStationDescription, generateStationSchema } from '../../../lib/utils/generateStationSchema';
import useBannerAds from '../../../lib/hooks/useBannerAds';
import { AD_SLOTS, ANALYTICS_EVENTS, BRANCH_CONFIG, BRANCH_IO_KEY, FUEL_TYPES } from '../../constants';
import PageContext from '../../context/page';
import StationContext from '../../context/station';
import AmenityPropType from '../../prop-types/amenity';
import FuelPropType from '../../prop-types/fuel';
import StationHoursPropType from '../../prop-types/hours';
import ReviewPropType from '../../prop-types/review';
import StationPropType from '../../prop-types/station';
import { Ad } from '../Ad';
import AmenitiesList from '../AmenitiesList';
import StationStatusHistory from '../StationStatusHistory';
import GasPriceCollection from '../GasPriceCollection';
import { ReviewCTABanner } from '../InlineBanners';
import MobileDownloadAd from '../MobileDownloadAd';
import NearbyStationsList from '../NearbyStationsList';
import OutageForm from '../OutageForm';
import OutageSocialShare from '../OutageSocialShare';
import OutageStatus from '../OutageStatus';
import Page from '../Page';
import BASIC_TEMPLATE from '../Page/Templates';
import ReviewsContainer from '../Reviews';
import HelmetComponent from '../StationHelmet';
import StationInfoBox from '../StationInfoBox';
import StationPriceReport from '../StationPriceReport';
import TopSpottersList from '../TopSpottersList';
import styles from './Station.module.css';

const cx = classnames.bind(styles);

const renderBranchIO = () => {
  // eslint-disable-next-line global-require
  const branch = require('branch-sdk');
  if (undefined !== branch) {
    branch.init(
      BRANCH_IO_KEY,
      { /* options */ },
      (err, data) => { // eslint-disable-line no-unused-vars
        if (err) {
          // do-nothing
        }
      },
    );

    branch.banner(BRANCH_CONFIG, {});
  }
};

export default function Station({
  amenities,
  fetchAmenitiesByStation,
  fetchFuelsByStation,
  fetchStationInfo,
  fuels,
  hours,
  isFetching,
  reviews,
  station,
  stationId,
  user,
}) {
  useEffect(() => {
    fetchFuelsByStation(stationId);
    renderBranchIO();
    scrollToTop();
  }, [fetchFuelsByStation, stationId]);

  useEffect(() => {
    const hasStationDetails = station && station.name && station.address?.locality && station.address?.region;
    if (!isFetching && !hasStationDetails) {
      fetchStationInfo(stationId);
    }
  }, [station, stationId, fetchStationInfo, isFetching]);

  useEffectWithDeepCompare(() => {
    // Check if we need to fetch info for any missing amenities
    const isMissingAmenities = !station?.amenities?.every(stationAmenity => amenities.map(amenity => amenity.id).includes(stationAmenity));
    if (isMissingAmenities) {
      // TODO: get fetch missing amenities rather than fetching them all for the station
      fetchAmenitiesByStation(stationId);
    }
  }, [amenities, fetchAmenitiesByStation, station.amenities, stationId]);

  const handleReportPosted = useCallback(() => {
    fetchFuelsByStation(stationId);
  }, [fetchFuelsByStation, stationId]);

  const logGasBuddyCardBannerClickEvent = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.GASBUDDY_CARD_BANNER_CLICKED, {
      Location: 'StationDetails',
    });
  }, []);

  const { address, emergency_status: eStatus = {} } = station;
  const stationAmenities = amenities.filter(amenity => station?.amenities?.includes(amenity.id));
  const dfpParams = useMemo(() => ({ station_id: stationId, station_brand: station.brand_name }), [stationId, station.brand_name]);
  const helmetObject = { station, reviews, fuels, amenities: stationAmenities };
  const sellsDiesel = fuels.some(f => f.displayName === FUEL_TYPES.DIESEL);
  const showOutageInfo = eStatus.has_diesel || eStatus.has_power || eStatus.has_gas;
  const stationHasCashPrices = station?.amenities?.some(amenity => amenity === 'cash_credit');

  const desktopBannerName = 'GasBuddy_SDP_728x90';
  const mobileBannerName = 'GasBuddy_MobileWeb_320x50_SDP';
  const bannerAds = useBannerAds(desktopBannerName, mobileBannerName);

  if (isFetching) {
    return (
      <Loader size="lg">
        Loading station...
      </Loader>
    );
  }

  if (!address?.locality || !address?.region) {
    return null;
  }

  const stationDescription = getStationDescription(station, reviews, fuels, amenities);
  const stationSchema = generateStationSchema({ station, reviews, fuels, amenities, hours });

  return (
    <React.Fragment>
      <Page
        name="Station_Details"
        meta={{
          SMID: stationId,
          Brand: station.brand_id,
          City: address.locality,
          State: address.region,
        }}
        activeNavKey="find_gas"
        {...BASIC_TEMPLATE}
        adParams={dfpParams}
        bannerAds={bannerAds}
        description={stationDescription}
        schema={stationSchema}
      >
        <PageContext.Provider value="Station_Details">
          <StationContext.Provider value={stationId}>
            {station.name ? HelmetComponent(helmetObject) : null}
            <div className={cx('stationDetails')}>
              <Container>
                <FlexGrid container padded>
                  <FlexGrid.Column desktop={8} tablet={12}>
                    <Visibility desktop={false} tablet={false}>
                      <MobileDownloadAd analyticsScreenName="Details_Page" />
                    </Visibility>
                    <StationInfoBox stationId={stationId} />
                    {showOutageInfo && (
                      <React.Fragment>
                        <Header as="h3" className={cx('header')}>Station Status</Header>
                        <Panel
                          color="white"
                          padded={false}
                          bordered
                          rounded
                        >
                          <FlexGrid>
                            <FlexGrid.Column tablet={7}>
                              <OutageStatus
                                powerReport={eStatus.has_power && {
                                  status: eStatus.has_power.report_status,
                                  username: eStatus.has_power.nick_name,
                                  timestamp: eStatus.has_power.update_date,
                                }}
                                gasReport={eStatus.has_gas && {
                                  status: eStatus.has_gas.report_status,
                                  username: eStatus.has_gas.nick_name,
                                  timestamp: eStatus.has_gas.update_date,
                                }}
                                dieselReport={(eStatus.has_diesel && {
                                  status: eStatus.has_diesel.report_status,
                                  username: eStatus.has_diesel.nick_name,
                                  timestamp: eStatus.has_diesel.update_date,
                                }) || (sellsDiesel && { status: true })}
                                sellsDiesel={sellsDiesel}
                              />
                            </FlexGrid.Column>
                            <FlexGrid.Column tablet={5}>
                              <Panel className={cx('outageCTAs')} color="white" bordered={false} rounded>
                                <Modal
                                  content={({ close }) => (
                                    <OutageForm
                                      stationId={stationId}
                                      onComplete={close}
                                      includeDiesel={sellsDiesel}
                                      onCancel={close}
                                    />
                                  )}
                                  size="sm"
                                >
                                  {({ open }) => (
                                    <Button
                                      primary
                                      onClick={open}
                                      fluid
                                    >
                                      Report Fuel or Power
                                    </Button>
                                  )}
                                </Modal>
                                <Modal
                                  content={({ close }) => (
                                    <OutageSocialShare
                                      status={eStatus}
                                      onComplete={close}
                                      includeDiesel={sellsDiesel}
                                    />
                                  )}
                                  size="sm"
                                >
                                  {({ open }) => (
                                    <Button
                                      secondary
                                      onClick={open}
                                      fluid
                                    >
                                      Share Station Status
                                    </Button>
                                  )}
                                </Modal>
                                <Modal
                                  content={({ close }) => (
                                    <React.Fragment>
                                      <Header as="h3">Station status history</Header>
                                      <StationInfoBox
                                        stationId={stationId}
                                        basic
                                        noContainer
                                        noRating
                                        noBadges
                                        noFavoriting
                                      />
                                      <Divider style={{ margin: 0 }} />
                                      <StationStatusHistory
                                        stationId={stationId}
                                        includeDiesel={sellsDiesel}
                                      />
                                      <Divider />
                                      <Button primary onClick={close}>
                                        Close
                                      </Button>
                                    </React.Fragment>
                                  )}
                                  size="sm"
                                >
                                  {({ open }) => (
                                    <Button
                                      secondary
                                      onClick={open}
                                      fluid
                                    >
                                      Station Status History
                                    </Button>
                                  )}
                                </Modal>
                              </Panel>
                            </FlexGrid.Column>
                          </FlexGrid>
                        </Panel>
                        <br />
                      </React.Fragment>
                    )}
                    <GasPriceCollection fuels={fuels} showCashPrices={stationHasCashPrices} />
                    {!user?.isPwGBMember && station.address?.country === 'US' && (
                      <a
                        href="https://enroll.gasbuddy.com"
                        onClick={logGasBuddyCardBannerClickEvent}
                      >
                        <Image
                          alt="Pay With GasBuddy Banner"
                          className={cx('pwgbBanner')}
                          fluid
                          src="https://static.gasbuddy.com/web/pay/pwgb-station-banner.png"
                        />
                      </a>
                    )}
                    <StationPriceReport
                      station={{ ...station, stationId }}
                      fuels={fuels}
                      onReportDone={handleReportPosted}
                    />
                    <br />
                    <AmenitiesList stationId={stationId} />
                    <ReviewsContainer stationId={stationId} />
                    <ReviewCTABanner />
                  </FlexGrid.Column>
                  <FlexGrid.Column desktop={4} tablet={12}>
                    <Ad
                      id={AD_SLOTS[4].slotID}
                      path="GasBuddy_SDP_300x250"
                      sizes={AD_SLOTS[4].sizes}
                      collapseIfEmpty
                    />
                    <NearbyStationsList stationId={stationId} />
                    <TopSpottersList stationId={stationId} />
                  </FlexGrid.Column>
                </FlexGrid>
              </Container>
            </div>
          </StationContext.Provider>
        </PageContext.Provider>
      </Page>
    </React.Fragment>
  );
}

Station.propTypes = {
  amenities: PropTypes.arrayOf(AmenityPropType),
  fetchAmenitiesByStation: PropTypes.func,
  fetchFuelsByStation: PropTypes.func,
  fetchStationInfo: PropTypes.func,
  fuels: PropTypes.arrayOf(FuelPropType),
  hours: StationHoursPropType,
  isFetching: PropTypes.bool,
  reviews: PropTypes.arrayOf(ReviewPropType),
  station: StationPropType,
  stationId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    isPwGBMember: PropTypes.bool.isRequired,
  }),
};

Station.defaultProps = {
  amenities: [],
  fetchAmenitiesByStation: noop,
  fetchFuelsByStation: noop,
  fetchStationInfo: noop,
  fuels: [],
  hours: null,
  isFetching: false,
  reviews: [],
  station: {
    name: '',
    numRatings: undefined,
    enterprise: false,
    address: {
      line_1: '',
      line_2: '',
      locality: '',
      region: '',
      country: '',
      postal_code: '',
      cross_street: '',
      cross_street_alias: '',
      address_alias: '',
      at_intersection: false,
      nearby_street: '',
      description: '',
    },
    phone: '',
  },
  user: null,
};
